import { useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Ruler, Palette, Layers, Maximize, ShoppingBag, ChevronRight, FileText } from "lucide-react";
import { StockBadge, StockLegend } from "@/components/StockBadge";

const categoryLabels = {
  all: "All Qualities",
  cambric: "Cambric Lining",
  pure_cotton: "Pure Cotton",
  crepe: "American Crepe",
  cut_piece: "Cut Piece",
};

export default function ProductCatalog({ products }) {
  const [activeTab, setActiveTab] = useState("all");

  const filtered =
    activeTab === "all"
      ? products
      : products.filter((p) => p.category === activeTab);

  return (
    <section
      id="products"
      className="py-20 lg:py-32"
      style={{ backgroundColor: "var(--mp-surface)" }}
      data-testid="product-catalog-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Header */}
        <div className="mb-12 lg:mb-16">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Premium Lining Fabrics
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="catalog-heading"
          >
            Our Product Range
          </h1>
          <div className="section-divider" />
          <p
            className="mt-4 text-base leading-relaxed max-w-2xl"
            style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}
          >
            Explore our comprehensive range of 9 premium fabric qualities plus
            ready cut-piece packs,
            each crafted to meet the exacting standards of India&apos;s finest garment makers.
          </p>

          {/* Quick actions strip */}
          <div className="mt-6 flex flex-wrap gap-3" data-testid="products-quick-actions">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-full border transition-colors"
              style={{
                borderColor: "var(--mp-solid)",
                color: "var(--mp-text-strong)",
                fontFamily: "'Manrope', sans-serif",
              }}
              data-testid="products-compare-cta"
            >
              <Layers className="w-3.5 h-3.5" /> Compare Fabrics
            </Link>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/products-static/print/rate_card_A4.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-full border transition-colors"
              style={{
                borderColor: "var(--mp-line)",
                color: "var(--mp-text)",
                fontFamily: "'Manrope', sans-serif",
              }}
              data-testid="products-rate-card-link"
            >
              <FileText className="w-3.5 h-3.5" /> Rate Card (A4 PDF)
            </a>
            <Link
              to="/sample-kit"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-xs font-semibold rounded-full text-white transition-all hover:shadow-md"
              style={{
                backgroundColor: "var(--mp-copper-solid)",
                fontFamily: "'Manrope', sans-serif",
                letterSpacing: "0.04em",
              }}
              data-testid="products-sample-kit-cta"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Order Sample Kit · ₹299
            </Link>
          </div>
        </div>

        <StockLegend className="mb-8" />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/70 backdrop-blur-sm border border-[var(--mp-line)] rounded-sm p-1 mb-10 flex-wrap h-auto gap-1" data-testid="product-tabs">
            {Object.entries(categoryLabels).map(([key, label]) => (
              <TabsTrigger
                key={key}
                value={key}
                className="rounded-sm text-sm font-medium px-5 py-2.5 data-[state=active]:bg-[var(--mp-solid)] data-[state=active]:text-white data-[state=active]:shadow-none"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid={`tab-${key}`}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.keys(categoryLabels).map((cat) => (
            <TabsContent key={cat} value={cat}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger-children">
                {(cat === "all" ? products : products.filter((p) => p.category === cat)).map(
                  (product, idx) => {
                    const orderTo = `/catalogue/${product.id}`;
                    return (
                    <div
                      key={product.id}
                      className="product-card border border-[var(--mp-line)] rounded-sm p-6 animate-fade-in-up flex flex-col"
                      style={{ animationDelay: `${idx * 80}ms`, backgroundColor: "var(--mp-card)" }}
                      data-testid={`product-card-${product.id}`}
                    >
                      <Link
                        to={`/product/${product.id}`}
                        className="block no-underline flex-1"
                        style={{ textDecoration: "none" }}
                        data-testid={`product-card-link-${product.id}`}
                      >
                      {/* Brand logo + fabric sample split */}
                      {product.logo_url && (
                        <div className="relative -mx-6 -mt-6 mb-5 border-b border-[var(--mp-line)] overflow-hidden">
                          <span className="absolute top-3 left-3 z-10">
                            <StockBadge status={product.stock_status || "ready"} />
                          </span>
                          <div className="aspect-square bg-white flex items-center justify-center">
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${product.logo_url}`}
                              alt={`${product.name} logo`}
                              className="w-full h-full object-contain p-4"
                              loading="lazy"
                            />
                          </div>
                          {product.sample_image_url && (
                            <div className="h-14 overflow-hidden border-t border-[var(--mp-line)]" data-testid={`product-fabric-strip-${product.id}`}>
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${product.sample_image_url}`}
                                alt={`${product.name} fabric sample`}
                                className="w-full h-full object-cover"
                                style={{ objectPosition: "center 40%" }}
                                loading="lazy"
                              />
                            </div>
                          )}
                          {product.badge && (
                            <span
                              className="absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full shadow-sm"
                              style={{
                                fontFamily: "'Manrope', sans-serif",
                                backgroundColor: "var(--mp-copper-solid)",
                                color: "var(--mp-text-strong)",
                                letterSpacing: "0.05em",
                              }}
                              data-testid={`product-badge-${product.id}`}
                            >
                              {product.badge}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Product name */}
                      <h3
                        className="text-xl md:text-2xl tracking-tight font-medium mb-1"
                        style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
                      >
                        {product.name}
                      </h3>
                      <p
                        className="text-xs uppercase tracking-[0.15em] font-semibold mb-4"
                        style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {product.dye_type} &bull; {product.material}
                      </p>

                      {/* Description */}
                      <p
                        className="text-sm leading-relaxed mb-5"
                        style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
                      >
                        {product.description}
                      </p>

                      {/* Specs grid */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
                          <span className="text-xs font-medium" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                            {product.fold} &middot; {product.cut_pack}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Maximize className="w-4 h-4 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
                          <span className="text-xs font-medium" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                            {product.panna} &middot; {product.width}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Palette className="w-4 h-4 flex-shrink-0" style={{ color: "var(--mp-text-strong)" }} />
                          <span className="text-xs font-medium" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                            {product.colors_available} colours available
                          </span>
                        </div>
                      </div>

                      {/* Color indicator bar */}
                      <div className="mt-5 h-1.5 rounded-full overflow-hidden bg-white/60">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(100, (product.colors_available / 446) * 100)}%`,
                            backgroundColor: "var(--mp-solid)",
                            transition: 'width 1s ease-out',
                          }}
                        />
                      </div>
                      <p className="text-[10px] mt-1 font-medium" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>
                        Colour range coverage
                      </p>
                    </Link>
                    {/* Browse colours CTA (jumps to Colour Catalogue pre-filtered by this quality) */}
                    <Link
                      to={`/catalogue/${product.id}`}
                      className="mt-4 inline-flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-sm border transition-colors hover:bg-[var(--mp-surface)]"
                      style={{
                        borderColor: "var(--mp-line)",
                        color: "var(--mp-text-strong)",
                        fontFamily: "'Manrope', sans-serif",
                      }}
                      data-testid={`product-browse-colours-${product.id}`}
                    >
                      <Palette className="w-3.5 h-3.5" />
                      Browse all {product.colors_available} colours <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                    {/* Order CTA */}
                    <Link
                      to={orderTo}
                      className="mt-5 inline-flex items-center justify-center gap-2 text-xs font-medium py-2.5 rounded-sm border transition-colors hover:bg-[var(--mp-solid)] hover:text-white hover:border-[var(--mp-solid)]"
                      style={{
                        borderColor: "var(--mp-solid)",
                        color: "var(--mp-text-strong)",
                        fontFamily: "'Manrope', sans-serif",
                      }}
                      data-testid={`product-order-btn-${product.id}`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5" />
                      Order by Shade Number
                    </Link>
                  </div>
                  );
                  }
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
